import { Component, OnDestroy, OnInit, resource, signal, computed } from '@angular/core';
import { DatePipe, Location } from '@angular/common';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { firstValueFrom, lastValueFrom, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { EcabSelectComponent, EcabButtonComponent, EcabInputComponent } from '@ecab/ecab-ui';
import {
  ApiClient,
  BuildingMode,
  CaseType,
  CollaboratorDto,
  MessageDto,
  MessageDtoQuestionType,
  MyEcabTownGroupDto,
  QuestionType2,
  OpenMessagesGroupedDto,
  PredefinedMessageFilterDto,
  PredefinedMessageFilterDtoTargetView,
  UpdateMessageCollaboratorDto
} from '../../services/api.clients.generated';
import {
  buildMessageFilterQueryParams,
  emptyMessageFilterState,
  inputValueToValues,
  readExplicitMessageFilterOverrides,
  readFilterId,
  readMessageFilterState,
  toOpenBuildingMode,
  toOpenCaseType,
  toOpenPersonMode,
  valuesToInputValue
} from '../message-filter-state';
import { NotificationService } from '../../notifications/notification.service';
import { getContactAvatarColor, getContactInitials } from '../contact-display.utils';
import { MarkdownExcerptPipe } from '../../shared/markdown/markdown-excerpt.pipe';

@Component({
  selector: 'app-open-messages',
  imports: [
    DatePipe,
    TranslatePipe,
    FormsModule,
    MatExpansionModule,
    MatIconModule,
    EcabInputComponent,
    EcabSelectComponent,
    EcabButtonComponent,
    MarkdownExcerptPipe
  ],
  templateUrl: './open-messages.component.html',
  styleUrl: './open-messages.component.scss'
})
export class OpenMessagesComponent implements OnInit, OnDestroy {
  assignedToMe: MessageDto[] = [];
  unassigned: MessageDto[] = [];
  assignedToOthers: MessageDto[] = [];
  loading = true;
  searchTerm = '';
  buildingIdFilter = '';
  buildingMode: BuildingMode = BuildingMode.ALL;
  caseIdFilter = '';
  townGroupIds: string[] = [];
  personIdFilter = '';
  personMode: BuildingMode = BuildingMode.ALL;
  caseTypeFilter: CaseType | undefined = undefined;
  questionTypeFilter?: MessageDtoQuestionType;
  predefinedFilters = signal<PredefinedMessageFilterDto[]>([]);
  townGroupOptions: { value: string; label: string }[] = [];
  advancedFiltersSectionExpanded = false;

  CaseType = CaseType;
  FilterMode = BuildingMode;
  QuickFilterTarget = PredefinedMessageFilterDtoTargetView;
  MessageQuestionType = MessageDtoQuestionType;

  editingCollaboratorId = signal<number | null>(null);
  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;
  private currentQueryParamMap: ParamMap;

  collaboratorsResource = resource<CollaboratorDto[], undefined>({
    loader: async () => {
      return await lastValueFrom(this.messageService.getCollaborators());
    }
  });

  collaboratorOptions = computed(() => {
    if (this.collaboratorsResource.hasValue()) {
      return this.collaboratorsResource.value();
    }
    return undefined;
  });

  constructor(
    private readonly messageService: ApiClient,
    protected readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly translateService: TranslateService,
    private readonly notificationService: NotificationService,
    protected readonly location: Location
  ) {
    this.currentQueryParamMap = this.route.snapshot.queryParamMap;
  }

  ngOnInit() {
    this.townGroupOptions = this.buildTownGroupOptions([]);
    void this.loadPredefinedFilters();
    void this.loadTownGroups();

    this.route.queryParamMap.subscribe(params => {
      this.currentQueryParamMap = params;
      this.syncStateFromRoute();
    });

    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => this.updateRouteWithFilters(true));
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
  }

  onSearchChange() {
    this.searchSubject.next(this.searchTerm);
  }

  loadOpenMessages() {
    this.loading = true;
    const explicitOverrides = readExplicitMessageFilterOverrides(this.currentQueryParamMap);
    const filterId = readFilterId(this.currentQueryParamMap);

    firstValueFrom(this.messageService.getOpenMessagesGrouped(
      filterId,
      explicitOverrides.search,
      explicitOverrides.buildingIds,
      explicitOverrides.buildingMode ? toOpenBuildingMode(explicitOverrides.buildingMode) : undefined,
      explicitOverrides.caseId,
      explicitOverrides.townGroupIds,
      explicitOverrides.personIds,
      explicitOverrides.personMode ? toOpenPersonMode(explicitOverrides.personMode) : undefined,
      explicitOverrides.caseType ? toOpenCaseType(explicitOverrides.caseType) : undefined,
      convertMessageQuestionTypeToQuestionType2(explicitOverrides.questionType)
    )).then(
      (response: OpenMessagesGroupedDto) => {
        this.assignedToMe = response.assignedToMe ?? [];
        this.unassigned = response.unassigned ?? [];
        this.assignedToOthers = response.assignedToOthers ?? [];
        this.loading = false;
      },
      error => {
        this.notificationService.error(
          this.notificationService.errorMessage(error, this.translateService.instant('openMessages.feedback.loadError'))
        );
        this.assignedToMe = [];
        this.unassigned = [];
        this.assignedToOthers = [];
        this.loading = false;
      }
    );
  }

  get searchSectionSummary(): string {
    const value = this.searchTerm.trim();
    return value
      ? this.translateService.instant('filter.workspace.searchSummaryValue', { value })
      : this.translateService.instant('filter.workspace.searchSummaryEmpty');
  }

  get advancedFiltersSectionSummary(): string {
    const summaries: string[] = [];

    if (this.buildingMode !== BuildingMode.ALL) {
      summaries.push(this.translateService.instant('filter.workspace.summary.buildingMode'));
    }
    if (this.buildingMode === BuildingMode.SELECTED_VALUES && inputValueToValues(this.buildingIdFilter).length > 0) {
      summaries.push(this.translateService.instant('filter.workspace.summary.buildingValues', {
        count: inputValueToValues(this.buildingIdFilter).length
      }));
    }
    if (this.caseIdFilter.trim()) {
      summaries.push(this.translateService.instant('filter.workspace.summary.caseId'));
    }
    if (this.townGroupIds.length > 0) {
      summaries.push(this.translateService.instant('filter.workspace.summary.townGroupId'));
    }
    if (this.personMode !== BuildingMode.ALL) {
      summaries.push(this.translateService.instant('filter.workspace.summary.personMode'));
    }
    if (this.personMode === BuildingMode.SELECTED_VALUES && inputValueToValues(this.personIdFilter).length > 0) {
      summaries.push(this.translateService.instant('filter.workspace.summary.personValues', {
        count: inputValueToValues(this.personIdFilter).length
      }));
    }
    if (this.caseTypeFilter !== undefined) {
      summaries.push(this.translateService.instant('filter.workspace.summary.caseType'));
    }
    if (this.questionTypeFilter !== undefined) {
      summaries.push(this.translateService.instant('filter.workspace.summary.questionType'));
    }

    if (summaries.length === 0) {
      return this.translateService.instant('filter.workspace.advancedSummaryEmpty');
    }

    return summaries.join(' · ');
  }

  applyFilters() {
    this.updateRouteWithFilters();
  }

  clearFilters() {
    const emptyState = emptyMessageFilterState();
    this.buildingIdFilter = '';
    this.buildingMode = emptyState.buildingMode;
    this.caseIdFilter = '';
    this.townGroupIds = [];
    this.personIdFilter = '';
    this.personMode = emptyState.personMode;
    this.caseTypeFilter = undefined;
    this.questionTypeFilter = undefined;
    this.updateRouteWithFilters();
  }

  onBuildingModeChange() {
    if (this.buildingMode !== BuildingMode.SELECTED_VALUES) {
      this.buildingIdFilter = '';
    }
  }

  onPersonModeChange() {
    if (this.personMode !== BuildingMode.SELECTED_VALUES) {
      this.personIdFilter = '';
    }
  }

  openMessage(msg: MessageDto) {
    this.router.navigate(['/messages', msg.id], {
      state: { message: msg }
    });
  }

  setSectionExpanded(section: 'advanced', expanded: boolean) {
    this.advancedFiltersSectionExpanded = expanded;
  }

  switchEditCollaborator(msgId: number, event: Event) {
    event.stopPropagation();
    if (this.editingCollaboratorId() === msgId) {
      this.editingCollaboratorId.set(null);
    } else {
      this.editingCollaboratorId.set(msgId);
      this.collaboratorsResource.reload();
    }
  }

  onCollaboratorChange(msg: MessageDto, value: any, event: Event) {
    if ('stopPropagation' in event) {
      event.stopPropagation();
    }
    const collaborator = value as CollaboratorDto | null;
    this.editingCollaboratorId.set(null);
    if (msg.id) {
      const body = new UpdateMessageCollaboratorDto();
      body.assignedCollaboratorId = collaborator?.id ?? '';
      body.assignedCollaboratorEmail = collaborator?.email ?? '';

      lastValueFrom(this.messageService.updateMessageCollaborator(msg.id, body)).then(
        () => {
          this.notificationService.success(this.translateService.instant('openMessages.feedback.collaboratorUpdated'));
          this.loadOpenMessages();
        },
        error => {
          this.notificationService.error(
            this.notificationService.errorMessage(error, this.translateService.instant('openMessages.feedback.collaboratorUpdateError'))
          );
        }
      );
    }
  }

  getInitials(msg: MessageDto): string {
    return getContactInitials(msg);
  }

  getContactDisplay(msg: MessageDto): string {
    if (msg.firstname && msg.lastname) {
      return `${msg.firstname} ${msg.lastname}`;
    }
    return msg.companyName || msg.email || this.translateService.instant('common.unknown');
  }

  getCollaboratorDisplay(msg: MessageDto): string {
    if (msg.assignedCollaboratorEmail) {
      return msg.assignedCollaboratorEmail;
    }
    return '';
  }

  getAvatarColor(msg: MessageDto): string {
    return getContactAvatarColor(msg);
  }

  getCollaboratorOptions(withUnassign: boolean) {
    const options: any[] = [];
    if (withUnassign) {
      options.push({ value: null, label: this.translateService.instant('openMessages.unassign') });
    }
    const collabs = this.collaboratorOptions() || [];
    for (const collab of collabs) {
      options.push({
        value: collab,
        label: `${collab.firstname} ${collab.lastname} (${collab.email})`
      });
    }
    return options;
  }

  getQuestionTypeOptions() {
    return [
      { value: MessageDtoQuestionType.MY_ACCOUNT, label: this.translateService.instant('message.questionType.ACCOUNT') },
      { value: MessageDtoQuestionType.GENERAL_QUESTION, label: this.translateService.instant('message.questionType.GENERAL') },
      { value: MessageDtoQuestionType.CLAIM, label: this.translateService.instant('message.questionType.CLAIM') },
      { value: MessageDtoQuestionType.PREVENTION, label: this.translateService.instant('message.questionType.PREVENTION') },
      { value: MessageDtoQuestionType.ESTIMATION, label: this.translateService.instant('message.questionType.ESTIMATION') },
      { value: MessageDtoQuestionType.BUILDINGS, label: this.translateService.instant('message.questionType.BUILDING') },
      { value: MessageDtoQuestionType.SUBSIDIES, label: this.translateService.instant('message.questionType.SUBSIDY') }
    ];
  }

  private async loadTownGroups() {
    try {
      const groups = await firstValueFrom(
        this.messageService.getEstimationTownGroups(this.translateService.currentLang || undefined)
      );
      this.townGroupOptions = this.buildTownGroupOptions(groups ?? []);
    } catch (error) {
      this.notificationService.warning(
        this.notificationService.errorMessage(error, this.translateService.instant('openMessages.feedback.townGroupsUnavailable'))
      );
      this.townGroupOptions = this.buildTownGroupOptions([]);
    }
  }

  private buildTownGroupOptions(groups: MyEcabTownGroupDto[]): { value: string; label: string }[] {
    return groups
      .filter(group => group.id !== undefined && group.id !== null)
      .map(group => ({ value: String(group.id), label: group.name ?? String(group.id) }));
  }

  private async loadPredefinedFilters() {
    try {
      const filters = await firstValueFrom(this.messageService.getPredefinedMessageFilters());
      this.predefinedFilters.set(filters ?? []);
    } catch (error) {
      this.notificationService.warning(
        this.notificationService.errorMessage(error, this.translateService.instant('openMessages.feedback.predefinedFiltersUnavailable'))
      );
      this.predefinedFilters.set([]);
    }
    this.syncStateFromRoute();
  }

  private updateRouteWithFilters(replaceUrl = false) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: buildMessageFilterQueryParams({
        search: this.searchTerm.trim(),
        buildingMode: this.buildingMode,
        buildingIds: this.buildingMode === BuildingMode.SELECTED_VALUES ? inputValueToValues(this.buildingIdFilter) : [],
        caseId: this.caseIdFilter.trim(),
        townGroupIds: this.townGroupIds,
        personMode: this.personMode,
        personIds: this.personMode === BuildingMode.SELECTED_VALUES ? inputValueToValues(this.personIdFilter) : [],
        caseType: this.caseTypeFilter,
        questionType: this.questionTypeFilter
      }),
      replaceUrl
    });
  }

  private syncStateFromRoute() {
    const state = readMessageFilterState(
      this.currentQueryParamMap,
      this.predefinedFilters(),
      PredefinedMessageFilterDtoTargetView.OPEN_MESSAGES
    );
    this.searchTerm = state.search;
    this.buildingMode = state.buildingMode;
    this.buildingIdFilter = valuesToInputValue(state.buildingIds);
    this.caseIdFilter = state.caseId;
    this.townGroupIds = state.townGroupIds;
    this.personMode = state.personMode;
    this.personIdFilter = valuesToInputValue(state.personIds);
    this.caseTypeFilter = state.caseType;
    this.questionTypeFilter = state.questionType;
    this.loadOpenMessages();
  }
}

function convertMessageQuestionTypeToQuestionType2(value?: MessageDtoQuestionType): QuestionType2 | undefined {
  if (!value) {
    return undefined;
  }
  return QuestionType2[value as keyof typeof QuestionType2];
}
